import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { PanelContainer } from '../../../../components/Panel'
import { InputNumberToggleable } from '../../../../components/inputs'
import { StatusTag } from '../../../../components/tableTemplates'
import { usePanelContext } from '../../../../contexts/panel'
import { useSelectFirstAndOnlyOption } from '../../../../hooks'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import { ConfigurableComponent } from '../../../../utils/wrapper'
import { DefaultConfig } from '../../../ui-config/data'
import { DataForConfirmationDto } from '../../data/model'
import { FormFieldName } from './DataForDeliveryForecastDespatchAdvice.form'

type Props = {
  data: DataForConfirmationDto
  config: DefaultConfig
  formik: FormikProps<DataForConfirmationDto>
  isFormFieldValid: (name: FormFieldName) => boolean
  getFormErrorMessage: (name: FormFieldName) => false | JSX.Element
  isLoading: boolean
}

export const DataForDeliveryForecastDespatchAdviceView: React.FC<Props> = ({
  data,
  config,
  formik,
  isFormFieldValid,
  getFormErrorMessage,
  isLoading,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n

  const panelId =
    'delivery-forecast-despatch-advice-data-for-delivery-forecast-despatch-advice'
  const { isEdit } = usePanelContext()

  // ************************************************************
  // components
  // ************************************************************
  // deliveryNoteNumber
  const deliveryNoteNumberLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('deliveryNoteNumber'),
      })}
      htmlFor="deliveryNoteNumber"
    ></label>
  )

  const deliveryNoteNumberComponent = (
    <InputText
      id="deliveryNoteNumber"
      value={formik.values.deliveryNoteNumber}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('deliveryNoteNumber'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const deliveryNoteNumberErrorMsg = (
    <div>{getFormErrorMessage('deliveryNoteNumber')}</div>
  )

  const deliveryNoteNumberWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={deliveryNoteNumberComponent}
      label={deliveryNoteNumberLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={deliveryNoteNumberErrorMsg}
      isLoading={isLoading}
    />
  )

  // deliveryDate
  const deliveryDateLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('deliveryDate'),
      })}
      htmlFor="deliveryDate"
    ></label>
  )

  const deliveryDateComponent = (
    <Calendar
      id="deliveryDate"
      value={
        formik.values.deliveryDate
          ? new Date(formik.values.deliveryDate)
          : undefined
      }
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('deliveryDate'),
      })}
      readOnlyInput
      showIcon
      locale={language}
      disabled={!isEdit(panelId)}
    />
  )

  const deliveryDateErrorMsg = <div>{getFormErrorMessage('deliveryDate')}</div>

  const deliveryDateWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={deliveryDateComponent}
      label={deliveryDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={deliveryDateErrorMsg}
      isLoading={isLoading}
    />
  )

  // deliveryType
  const deliveryTypeLabel = (
    <label className="text-sm" htmlFor="deliveryType"></label>
  )

  const deliveryTypeComponent = (
    <InputText
      id="deliveryType"
      value={
        data?.deliveryType === 'FULL'
          ? t('common.codes.deliveryType.full')
          : t('common.codes.deliveryType.partly')
      }
      className="w-full"
      disabled
    />
  )

  const deliveryTypeWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={deliveryTypeComponent}
      label={deliveryTypeLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )
  // confirmationCode
  const confirmationCodeLabel = (
    <label className="text-sm w-full" htmlFor="confirmationCode"></label>
  )

  const confirmationCodeComponent = (
    <StatusTag
      id="confirmationCode"
      status={data.confirmationCode}
      className="p-2"
    />
  )

  const confirmationCodeWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={confirmationCodeComponent}
      label={confirmationCodeLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationId
  const confirmationIdLabel = (
    <label className="text-sm" htmlFor="confirmationId"></label>
  )

  const confirmationIdComponent = (
    <InputText
      id="confirmationId"
      value={formik.values.confirmationId}
      className="w-full"
      disabled
    />
  )

  const confirmationIdWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={confirmationIdComponent}
      label={confirmationIdLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationDate
  const confirmationDateLabel = (
    <label className="text-sm" htmlFor="confirmationDate"></label>
  )

  const confirmationDateComponent = (
    <Calendar
      id="confirmationDate"
      value={new Date(data.confirmationDate!)}
      className="w-full"
      readOnlyInput
      showIcon
      locale={language}
      disabled
    />
  )

  const confirmationDateWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={confirmationDateComponent}
      label={confirmationDateLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // termsOfDeliveryCode
  const termsOfDeliveryCodeLabel = (
    <label className="text-sm" htmlFor="termsOfDeliveryCode"></label>
  )

  useSelectFirstAndOnlyOption(
    'termsOfDeliveryCode',
    config.deliveryForecastDespatchAdvice.dataForDeliveryForecastDespatchAdvice
      .termsOfDeliveryCode.options,
    formik,
    isEdit(panelId),
  )

  const termsOfDeliveryCodeComponent = (
    <Dropdown
      id="termsOfDeliveryCode"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('termsOfDeliveryCode'),
      })}
      value={formik.values.termsOfDeliveryCode}
      onChange={e => formik.setFieldValue('termsOfDeliveryCode', e.value)}
      optionLabel="name.de"
      optionValue="code"
      disabled={!isEdit(panelId)}
    />
  )

  const termsOfDeliveryCodeWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={termsOfDeliveryCodeComponent}
      label={termsOfDeliveryCodeLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // confirmationGrossWeight
  const confirmationGrossWeightLabel = (
    <label
      htmlFor="confirmationGrossWeight"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationGrossWeight'),
      })}
    ></label>
  )

  const confirmationGrossWeightComponent = (
    <InputNumberToggleable
      id="confirmationGrossWeight"
      checked={formik.values.confirmationGrossWeightChecked}
      onCheckedChange={e =>
        formik.setFieldValue('confirmationGrossWeightChecked', e.checked)
      }
      value={formik.values.confirmationGrossWeight}
      mode="decimal"
      locale={language}
      onChange={e => formik.setFieldValue('confirmationGrossWeight', e.value)}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationGrossWeight'),
      })}
      min={0}
      minFractionDigits={3}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationGrossWeightErrorMsg = (
    <div>{getFormErrorMessage('confirmationGrossWeight')}</div>
  )

  const confirmationGrossWeightWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={confirmationGrossWeightComponent}
      label={confirmationGrossWeightLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationGrossWeightErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationGrossWeightUnit
  const confirmationGrossWeightUnitLabel = (
    <label
      htmlFor="confirmationGrossWeightUnit"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationGrossWeightUnit'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'confirmationGrossWeightUnit',
    config.deliveryForecastDespatchAdvice.dataForDeliveryForecastDespatchAdvice
      .confirmationGrossWeightUnit.options,
    formik,
    isEdit(panelId),
  )

  const confirmationGrossWeightUnitComponent = (
    <Dropdown
      id="confirmationGrossWeightUnit"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationGrossWeightUnit'),
      })}
      value={formik.values.confirmationGrossWeightUnit}
      onChange={e =>
        formik.setFieldValue('confirmationGrossWeightUnit', e.value)
      }
      optionLabel="code"
      optionValue="code"
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationGrossWeightUnitErrorMsg = (
    <div>{getFormErrorMessage('confirmationGrossWeightUnit')}</div>
  )

  const confirmationGrossWeightUnitWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={confirmationGrossWeightUnitComponent}
      label={confirmationGrossWeightUnitLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationGrossWeightUnitErrorMsg}
      isLoading={isLoading}
    />
  )

  // confirmationNumberOfUnits
  const confirmationNumberOfUnitsLabel = (
    <label
      htmlFor="confirmationNumberOfUnits"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationNumberOfUnits'),
      })}
    ></label>
  )

  const confirmationNumberOfUnitsComponent = (
    <InputNumberToggleable
      id="confirmationNumberOfUnits"
      checked={formik.values.confirmationNumberOfUnitsChecked}
      onCheckedChange={e =>
        formik.setFieldValue('confirmationNumberOfUnitsChecked', e.checked)
      }
      value={formik.values.confirmationNumberOfUnits}
      onChange={e => formik.setFieldValue('confirmationNumberOfUnits', e.value)}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationNumberOfUnits'),
      })}
      allowEmpty={false}
      locale={language}
      disabled={!isEdit(panelId)}
    />
  )

  const confirmationNumberOfUnitsErrorMsg = (
    <div>{getFormErrorMessage('confirmationNumberOfUnits')}</div>
  )

  const confirmationNumberOfUnitsWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={confirmationNumberOfUnitsComponent}
      label={confirmationNumberOfUnitsLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={confirmationNumberOfUnitsErrorMsg}
      isLoading={isLoading}
    />
  )

  // plant
  const plantLabel = <label className="text-sm" htmlFor="plant"></label>

  const plantComponent = (
    <InputText
      id="plant"
      value={formik.values.plant}
      className="w-full"
      disabled
    />
  )

  const plantWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={plantComponent}
      label={plantLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // unloadingPoint
  const unloadingPointLabel = (
    <label className="text-sm" htmlFor="unloadingPoint"></label>
  )

  const unloadingPointComponent = (
    <InputText
      id="unloadingPoint"
      value={formik.values.unloadingPoint}
      className="w-full"
      disabled
    />
  )

  const unloadingPointWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={unloadingPointComponent}
      label={unloadingPointLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      isLoading={isLoading}
    />
  )

  // GLN
  const glnLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('gln'),
      })}
      htmlFor="gln"
    ></label>
  )

  const glnComponent = (
    <InputText
      id="gln"
      value={formik.values.gln}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('gln'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const glnErrorMsg = <div>{getFormErrorMessage('gln')}</div>

  const glnWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={glnComponent}
      label={glnLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={glnErrorMsg}
      isLoading={isLoading}
    />
  )

  // company
  const companyLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('company'),
      })}
      htmlFor="company"
    ></label>
  )

  const companyComponent = (
    <InputText
      id="company"
      value={formik.values.company}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('company'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const companyErrorMsg = <div>{getFormErrorMessage('company')}</div>

  const companyWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={companyComponent}
      label={companyLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={companyErrorMsg}
      isLoading={isLoading}
    />
  )

  // department
  const departmentLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('department'),
      })}
      htmlFor="department"
    ></label>
  )

  const departmentComponent = (
    <InputText
      id="department"
      value={formik.values.department}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('department'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const departmentErrorMsg = <div>{getFormErrorMessage('department')}</div>

  const departmentWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={departmentComponent}
      label={departmentLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={departmentErrorMsg}
      isLoading={isLoading}
    />
  )

  // name
  const nameLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('name'),
      })}
      htmlFor="name"
    ></label>
  )

  const nameComponent = (
    <InputText
      id="name"
      value={formik.values.name}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': isFormFieldValid('name'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const nameErrorMsg = <div>{getFormErrorMessage('name')}</div>

  const nameWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={nameComponent}
      label={nameLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={nameErrorMsg}
      isLoading={isLoading}
    />
  )

  // phone
  const phoneLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('phone'),
      })}
      htmlFor="phone"
    ></label>
  )

  const phoneComponent = (
    <InputText
      id="phone"
      value={formik.values.phone}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('phone'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const phoneErrorMsg = <div>{getFormErrorMessage('phone')}</div>

  const phoneWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={phoneComponent}
      label={phoneLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={phoneErrorMsg}
      isLoading={isLoading}
    />
  )

  // email
  const emailLabel = (
    <label
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('email'),
      })}
      htmlFor="email"
    ></label>
  )

  const emailComponent = (
    <InputText
      id="email"
      value={formik.values.email}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('email'),
      })}
      disabled={!isEdit(panelId)}
    />
  )

  const emailErrorMsg = <div>{getFormErrorMessage('email')}</div>

  const emailWrapper = (
    <ConfigurableComponent
      config={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice
      }
      component={emailComponent}
      label={emailLabel}
      className="col-12 md:col-6 lg:col-4 xl:col-3"
      errorMsg={emailErrorMsg}
      isLoading={isLoading}
    />
  )

  return (
    <PanelContainer
      formik={formik}
      header={getConfigTranslation(
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice.title,
      )}
      id={panelId}
      visible={
        config.deliveryForecastDespatchAdvice
          .dataForDeliveryForecastDespatchAdvice.enabled !== false
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="formgrid grid">
          {/* deliveryNoteNumber */}
          {deliveryNoteNumberWrapper}

          {/* deliveryDateWrapper */}
          {deliveryDateWrapper}

          {/* deliveryType */}
          {deliveryTypeWrapper}

          {/* confirmationCode */}
          {confirmationCodeWrapper}

          <div className="col-12">
            <div className="formgrid grid">
              {/* confirmationId */}
              {confirmationIdWrapper}
              {/* confirmationDateWrapper */}
              {confirmationDateWrapper}
              {/* termsOfDeliveryCode */}
              {termsOfDeliveryCodeWrapper}
            </div>
          </div>

          <div className="col-12">
            <div className="formgrid grid">
              {/* confirmationGrossWeight */}
              {confirmationGrossWeightWrapper}

              {/* confirmationGrossWeightUnit */}
              {confirmationGrossWeightUnitWrapper}

              {/* confirmationNumberOfUnits */}
              {confirmationNumberOfUnitsWrapper}
            </div>
          </div>

          {/* plant */}
          {plantWrapper}

          {/* unloadingPoint */}
          {unloadingPointWrapper}

          {/* gln */}
          {glnWrapper}

          {/* company */}
          {companyWrapper}

          {/* department */}
          {departmentWrapper}

          {/* name */}
          {nameWrapper}

          {/* phone */}
          {phoneWrapper}

          {/* email */}
          {emailWrapper}
        </div>
      </form>
    </PanelContainer>
  )
}
